import { Component, effect, inject, OnInit } from '@angular/core';
import { DividerModule } from 'primeng/divider';
import { DropdownModule } from 'primeng/dropdown';
import { FormFieldWrapperComponent } from '../../molecules/form-field-wrapper/form-field-wrapper.component';
import { InputSwitchModule } from 'primeng/inputswitch';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { NgIf } from '@angular/common';
import { PaginatorModule } from 'primeng/paginator';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { SharedModule } from 'primeng/api';
import { BookmarkCategoryStore } from '../../../stores/bookmark-category/bookmark-category.state';
import { BookmarkCategory } from '../../../models/bookmark-category';
import { AppUtilityService } from '../../../utility/app.utility';
import { MultiSelectModule } from 'primeng/multiselect';
import { WorkspaceDropdownComponent } from '../../atoms/workspace-dropdown/workspace-dropdown.component';
import { BaseFormComponent } from '../../molecules/base-form/base-form.component';
import { ActivatedRoute, Router } from '@angular/router';
import { RoutingService } from '../../../services/routing.service';
import { ActiveUserStore } from '../../../stores/user/active-user.state';
import { UserDropdownComponent } from '../../atoms/user-dropdown/user-dropdown.component';
import { getState } from '@ngrx/signals';
import { PermissionService } from '../../../services/permission.service';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { FormFooterComponent } from '../../molecules/form-footer/form-footer.component';
import { PickListModule } from 'primeng/picklist';
import { HeaderComponent } from '../../atoms/header/header.component';
import { BookmarkStore } from '../../../stores/bookmark/bookmark.state';
import { Bookmark } from '../../../models/bookmark';

@Component({
  selector: 'app-bookmark-category-form',
  standalone: true,
  imports: [
    DividerModule,
    DropdownModule,
    FormFieldWrapperComponent,
    InputSwitchModule,
    InputTextModule,
    InputTextareaModule,
    NgIf,
    PaginatorModule,
    ReactiveFormsModule,
    SharedModule,
    MultiSelectModule,
    WorkspaceDropdownComponent,
    UserDropdownComponent,
    FormFooterComponent,
    PickListModule,
    HeaderComponent,
  ],
  templateUrl: './bookmark-category-form.component.html',
  styleUrl: './bookmark-category-form.component.scss',
})
export class BookmarkCategoryFormComponent
  extends BaseFormComponent<BookmarkCategory>
  implements OnInit
{
  readonly store = inject(BookmarkCategoryStore);
  readonly activeUserStore = inject(ActiveUserStore);
  readonly bookmarkStore = inject(BookmarkStore);

  override form: FormGroup = this.fb.group({});
  override itemToEdit?: BookmarkCategory;
  override formInitialized = false;

  // Pick-list state: bookmarks not (yet) in this category vs. bookmarks assigned to it.
  bookmarks: Bookmark[] = [];
  categoryBookmarks: Bookmark[] = [];
  private pendingBookmarkAssignment = false;

  constructor(
    router: Router,
    route: ActivatedRoute,
    appUtilityService: AppUtilityService,
    routingService: RoutingService,
    permissionService: PermissionService,
    confirmationDialogService: ConfirmationDialogService,
    private fb: FormBuilder
  ) {
    super(
      router,
      route,
      appUtilityService,
      routingService,
      permissionService,
      confirmationDialogService
    );
    effect(() => {
      const state = getState(this.store);
      if (state.selectedId) {
        this.initializeForm();
      }
    });

    // A newly created category only gets its _id once the server responds,
    // so the bookmark assignments picked in the pick-list have to wait for that.
    this.onceCreated(
      () => this.pendingBookmarkAssignment,
      id => {
        this.pendingBookmarkAssignment = false;
        this.applyBookmarkAssignments(id);
      }
    );
  }

  override ngOnInit() {
    super.ngOnInit();
    if (!this.formInitialized) this.initializeForm();
  }

  /**
   * Initializes the form with predefined values from the item to edit, user store,
   * and other properties. Configures the reactive form structure with predefined
   * validators and default values.
   *
   * @return {void} Does not return a value. Sets up the form and initializes it within the component.
   */
  initializeForm(): void {
    this.itemToEdit = this.store.selectedItem();

    if (!this.bookmarks.length) {
      const assignableBookmarks = this.bookmarkStore
        .sorted()
        .filter(bookmark => !bookmark.private);

      this.categoryBookmarks = this.itemToEdit
        ? assignableBookmarks.filter(
            bookmark => bookmark.category === this.itemToEdit!._id
          )
        : [];
      this.bookmarks = assignableBookmarks.filter(
        bookmark =>
          !this.categoryBookmarks.some(
            assigned => assigned._id === bookmark._id
          )
      );
    }

    this.form = this.fb.group({
      workspaces: [this.itemToEdit?.workspaces || ''],
      title: [this.itemToEdit?.title || '', Validators.required],
      private: [this.itemToEdit?.private || false],
      sorting: [this.itemToEdit?.sorting || this.store.count()],
      privateUsers: [
        this.itemToEdit?.privateUsers || [this.activeUserStore.user()?._id],
      ],
    });

    this.formInitialized = true;
  }

  /**
   * Handles the submission process by invoking the submit method. If the category
   * already exists, the pick-list assignments are applied right away; for a new
   * category they're deferred until its id comes back from the server.
   *
   * @return {void} Does not return any value.
   */
  onSubmit(): void {
    if (this.itemToEdit && this.itemToEdit._id) {
      this.submit();
      this.applyBookmarkAssignments(this.itemToEdit._id);
    } else {
      this.pendingBookmarkAssignment = true;
      this.submit();
    }
  }

  /**
   * Reassigns bookmarks to (or away from) this category based on the current
   * pick-list state. `Bookmark.category` remains the single source of truth for
   * the bookmark-category relationship, so the pick-list is just a bulk-edit
   * convenience on top of it. Every bookmark left in the "Auswahl" list also
   * gets its `sorting` reindexed to the pick-list's current order, so drag
   * reordering within the category is persisted too - not just add/remove.
   *
   * @param {string} categoryId - The id of the category to assign/unassign bookmarks to/from.
   * @return {void} Does not return a value.
   */
  private applyBookmarkAssignments(categoryId: string): void {
    const assigned = this.categoryBookmarks.map((bookmark, index) => ({
      ...bookmark,
      category: categoryId,
      sorting: index,
    }));

    // `category: undefined` is dropped by JSON.stringify, so the server never
    // receives that key. That's fine specifically for updateMultiple/updateMany:
    // the backend rebuilds each item as a fresh document and falls back to the
    // schema default (null) for any missing field, which does clear it in the DB.
    // The single-item update endpoint has no such fallback and would silently
    // keep the old category instead - don't reuse this against that endpoint.
    const unassigned = this.bookmarks
      .filter(bookmark => bookmark.category === categoryId)
      .map(bookmark => ({ ...bookmark, category: undefined }));

    const changedBookmarks = [...assigned, ...unassigned];
    if (changedBookmarks.length) {
      this.bookmarkStore.updateMultiple({ items: changedBookmarks });
    }
  }
}
